
import {
  ControlElement,
  JsonFormsRendererRegistryEntry,
  rankWith,
  and,
  schemaTypeIs,
  schemaMatches,
  Resolve,
  update,
} from "@jsonforms/core";
import { defineComponent, inject } from "vue";
import { rendererProps, useJsonFormsArrayControl } from "@jsonforms/vue";
import { ElTooltip } from "element-plus";
import RendererConfigurations from "@/core/json-forms/renderer-configurations";

const controlRenderer = defineComponent({
  name: RendererConfigurations.RepeatableStringControlRenderer.name,
  components: {
    ElTooltip,
  },
  props: {
    ...rendererProps<ControlElement>(),
  },
  setup(props: any) {
    const styles: any = inject("styles");
    const dispatch: any = inject("dispatch");
    return { ...useJsonFormsArrayControl(props), styles, dispatch };
  },
  computed: {
    RendererConfigurations() {
      return RendererConfigurations;
    },
    // The array (property) schema; control.schema is the *items* schema.
    arraySchema(): any {
      try {
        return (
          Resolve.schema(
            this.control.rootSchema,
            this.control.uischema.scope,
            this.control.rootSchema
          ) || {}
        );
      } catch (e) {
        return {};
      }
    },
    // Normalize to an array so a legacy scalar value still renders as a single row.
    items(): any[] {
      const { data } = this.control;
      if (Array.isArray(data)) {
        return data;
      }
      return data === undefined || data === null || data === "" ? [] : [data];
    },
    placeholder(): string {
      return this.control.schema?.placeholder || "";
    },
    pattern(): string | undefined {
      return this.control.schema?.pattern;
    },
    tooltip(): string | undefined {
      return this.arraySchema?.tooltip;
    },
    minItems(): number {
      return this.arraySchema?.minItems ?? 0;
    },
    maxItems(): number {
      return this.arraySchema?.maxItems ?? Number.MAX_SAFE_INTEGER;
    },
    isAddDisabled(): boolean {
      return this.items.length >= this.maxItems;
    },
    isRemoveDisabled(): boolean {
      return this.items.length <= this.minItems;
    },
    dataIdPrefix(): string {
      return (this.control.path || "repeatable").replace(/\./g, "-");
    },
  },
  methods: {
    isItemInvalid(element: any): boolean {
      const value = element == null ? "" : String(element);
      if (value.trim() === "") {
        // An empty entry can never satisfy a required field or a value pattern.
        return this.control.required || !!this.pattern;
      }
      if (this.pattern) {
        try {
          return !new RegExp(this.pattern).test(value);
        } catch (e) {
          return false;
        }
      }
      return false;
    },
    onItemInput(index: number, event: Event): void {
      const { value } = event.target as HTMLInputElement;
      this.dispatch(
        update(this.control.path, (data: any) => {
          const arr = Array.isArray(data) ? [...data] : [];
          arr[index] = value;
          return arr;
        })
      );
    },
    addRow(): void {
      this.addItem(this.control.path, "")();
    },
    removeRow(index: number): void {
      this.removeItems(this.control.path, [index])();
    },
  },
});

export default controlRenderer;

export const entry: JsonFormsRendererRegistryEntry = {
  renderer: controlRenderer,
  tester: rankWith(
    20,
    and(
      schemaTypeIs("array"),
      schemaMatches((schema: any) => !!schema && schema.repeatable === true)
    )
  ),
};
